import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Overline } from "./Reveal";

export const PageHero = ({ overline, title, highlight, subtitle, media, mediaType = "video", poster }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const words = String(title).split(" ");

  return (
    <section ref={ref} className="relative min-h-[78vh] flex items-end overflow-hidden bg-navy" data-testid="page-hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {media ? (
          mediaType === "video" ? (
            <video autoPlay muted loop playsInline poster={poster} className="w-full h-full object-cover" data-testid="page-hero-media">
              <source src={media} type="video/mp4" />
            </video>
          ) : (
            <img src={media} alt="" className="w-full h-full object-cover" data-testid="page-hero-media" />
          )
        ) : null}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f3159] via-[#164275]/55 to-[#164275]/70" />
      <div className="absolute inset-0 grain" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[var(--secondary)] opacity-25 blur-[120px]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 lg:pb-28 pt-40">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Overline dark>{overline}</Overline>
        </motion.div>
        <h1 className="mt-6 font-display font-light text-white tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
          <span className="block overflow-hidden py-1">
            <span className="inline-block">
              {words.map((w, i) => (
                <motion.span key={w + i} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }} className="inline-block mr-[0.25em]">{w}</motion.span>
              ))}
              {highlight && (
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.15 + words.length * 0.06, ease: [0.22, 1, 0.36, 1] }} className="inline-block italic font-normal text-accent-2">{highlight}</motion.span>
              )}
            </span>
          </span>
        </h1>
        {subtitle && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.5 }} className="mt-6 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed">{subtitle}</motion.p>
        )}
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 text-white/50" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
};
